// userCodeFrameBoot.js — the bootstrap that runs INSIDE the opaque user-code
// frame (USER-CODE-ORIGIN; design: workspace/docs/user-code-origin-design.md).
//
// The host (userCodeFrame.js) serializes `userCodeFrameBoot` with
// .toString() into the frame's static srcdoc document, so the function must be
// SELF-CONTAINED: no closure over this module, no imports, ES2017 syntax only
// (no object spread, no optional chaining, no async/await, no optional catch
// binding) so no bundler helper is referenced by the serialized text.
//
// What it does:
//   • gives the project an in-memory localStorage/sessionStorage and an empty
//     cookie jar (an opaque origin throws on all three; Nika 2026-10-09 Q1:
//     theme/lang inside a tile reset on each load);
//   • takes the MessageChannel port from the FIRST boot message the parent
//     sends (and never another);
//   • mounts the posted project with the smbls IIFE the document loaded first
//     (`window.Smbls.create`), exactly the create() options createIframeApp
//     builds — but in THIS realm, so project code never runs in the parent;
//   • adds the consumer's frame kit (`kit`: self-contained pure helpers as
//     CODE, design review D1) at the lowest priority;
//   • exposes to the project ONLY the parent's call table — one stub per
//     name the payload lists, plus the project's own db/fetch adapter — every
//     call is a port message the parent may refuse;
//   • answers the parent's mount / theme / dir / navigate / tool messages
//     (the literals of USER_CODE_MESSAGES in userCodeFrame.js).
//
// It holds no capability beyond the port. It never reads the parent.

export function userCodeFrameBoot(win, makeCompat, makeStubs, kit) {
  'use strict'
  var booted = false
  var doc = win.document

  function memoryStorage() {
    var m = Object.create(null)
    var api = {
      getItem: function (k) {
        k = String(k)
        return Object.prototype.hasOwnProperty.call(m, k) ? m[k] : null
      },
      setItem: function (k, v) {
        m[String(k)] = String(v)
      },
      removeItem: function (k) {
        delete m[String(k)]
      },
      clear: function () {
        for (var k in m) delete m[k]
      },
      key: function (i) {
        var ks = Object.keys(m)
        return i >= 0 && i < ks.length ? ks[i] : null
      }
    }
    Object.defineProperty(api, 'length', {
      get: function () {
        return Object.keys(m).length
      }
    })
    return api
  }

  function storageWorks(k) {
    try {
      var s = win[k]
      if (!s || typeof s.getItem !== 'function') return false
      s.getItem('__probe__')
      return true
    } catch (e) {
      return false
    }
  }

  function shimStorage() {
    var keys = ['localStorage', 'sessionStorage']
    for (var i = 0; i < keys.length; i++) {
      if (storageWorks(keys[i])) continue
      try {
        Object.defineProperty(win, keys[i], { value: memoryStorage(), configurable: true })
      } catch (e) {}
    }
    var cookieOk = true
    try {
      var c = doc.cookie
      cookieOk = typeof c === 'string'
    } catch (e) {
      cookieOk = false
    }
    if (!cookieOk) {
      try {
        Object.defineProperty(doc, 'cookie', {
          get: function () {
            return ''
          },
          set: function () {},
          configurable: true
        })
      } catch (e) {}
    }
  }

  function start(port) {
    var nextId = 1
    var pending = {}
    var app = null
    var themeCfg = null
    var errorsSent = 0
    // Editor tools the PARENT runs in this realm (the preview element picker):
    // a self-contained function `(api) => stop`, sent as text. It gets this
    // frame's window/document and `emit(data)` — plain data back over the
    // port. The frame is untrusted already; parent code here grants nothing.
    var tools = {}

    function stopTool(name) {
      var stop = tools[name]
      delete tools[name]
      if (typeof stop === 'function') {
        try {
          stop()
        } catch (e) {}
      }
    }

    function notify(method, args) {
      try {
        port.postMessage({ method: method, args: args || {} })
      } catch (e) {}
    }

    function call(method, args) {
      return new Promise(function (resolve, reject) {
        var id = nextId++
        pending[id] = function (m) {
          if (m.ok) resolve(m.result === undefined ? null : m.result)
          else reject(new Error(String(m.error || 'user_code_call_refused')))
        }
        port.postMessage({ id: id, method: method, args: args })
      })
    }

    // The project's own db/fetch adapter, served by the parent from the
    // project's OWN public config (never a Symbols session). Data methods only.
    function adapterProxy(which) {
      function m(name) {
        return function (c) {
          return call('db', { which: which, method: name, args: plainArgs(c) || {} }).then(
            function (r) {
              return r || { data: null, error: null }
            },
            function (err) {
              return { data: null, error: { message: String(err && err.message) } }
            }
          )
        }
      }
      return {
        name: 'user-code-proxy',
        select: m('select'),
        rpc: m('rpc'),
        insert: m('insert'),
        update: m('update'),
        upsert: m('upsert'),
        delete: m('delete')
      }
    }

    // The parent's call table, as context functions: one stub per name the
    // payload lists. A stub sends plain data (a value that cannot be cloned
    // rejects here, in the frame) and never holds a parent function.
    function plainArgs(list) {
      try {
        return JSON.parse(JSON.stringify(list))
      } catch (e) {
        return null
      }
    }
    function stubsFor(names) {
      var out = {}
      for (var i = 0; i < names.length; i++) {
        ;(function (name) {
          out[name] = function () {
            var args = plainArgs(Array.prototype.slice.call(arguments))
            if (!args) return Promise.reject(new Error('user_code_args_not_plain'))
            return call(name, args)
          }
        })(names[i])
      }
      return out
    }

    // The consumer's frame kit, built ONCE in this realm. A factory gets the
    // frame's own runtime (`{ Smbls, window }`) and returns a bag of helpers.
    var kitFunctions = {}
    function buildKit() {
      var fk = (kit && kit.functions) || {}
      for (var n in fk) kitFunctions[n] = fk[n]
      var facs = (kit && kit.factories) || []
      for (var j = 0; j < facs.length; j++) {
        try {
          var bag = facs[j]({ Smbls: win.Smbls, window: win })
          for (var b in bag) if (typeof bag[b] === 'function') kitFunctions[b] = bag[b]
        } catch (e) {
          reportError('frame kit factory ' + j + ': ' + ((e && e.message) || e))
        }
      }
    }

    function reportError(message) {
      if (errorsSent >= 20) return
      errorsSent++
      notify('error', { message: String(message).slice(0, 500) })
    }
    win.addEventListener('error', function (e) {
      reportError((e && e.error && e.error.message) || (e && e.message) || 'error')
    })
    win.addEventListener('unhandledrejection', function (e) {
      reportError((e && e.reason && e.reason.message) || String(e && e.reason))
    })

    function revive(v) {
      var S = win.Smbls
      if (!v || typeof v !== 'object' || !S || typeof S.deepDestringifyFunctions !== 'function') {
        return v
      }
      return S.deepDestringifyFunctions(v, Array.isArray(v) ? [] : {})
    }

    function clearBody() {
      var b = doc.body
      if (!b) return
      while (b.firstChild) b.removeChild(b.firstChild)
    }

    function mount(p) {
      var S = win.Smbls
      if (!S || typeof S.create !== 'function') {
        notify('boot-error', { code: 'user_code_runtime_missing' })
        return
      }
      if (app && typeof app.destroy === 'function') {
        try {
          app.destroy()
        } catch (e) {}
      }
      app = null
      themeCfg = null
      clearBody()
      if (p.spriteHtml && doc.body) {
        doc.body.insertAdjacentHTML(
          'afterbegin',
          '<svg aria-hidden="true" width="0" height="0" style="position:absolute" id="svgSprite">' +
            p.spriteHtml +
            '</svg>'
        )
      }
      var project = revive(p.project || {}) || {}
      var shell = revive(p.shell || {}) || {}
      var ov = revive(p.overrides || {}) || {}
      var unset = Array.isArray(p.unset) ? p.unset : []
      var adapters = p.adapters || {}

      var opts = {}
      var k
      for (k in project) {
        if (Object.prototype.hasOwnProperty.call(project, k)) opts[k] = project[k]
      }
      for (k in ov) {
        if (Object.prototype.hasOwnProperty.call(ov, k)) opts[k] = ov[k]
      }
      for (var u = 0; u < unset.length; u++) opts[unset[u]] = undefined

      var cfgKeys = ['fetch', 'db']
      for (var a = 0; a < cfgKeys.length; a++) {
        var ck = cfgKeys[a]
        if (adapters[ck] && opts[ck] && typeof opts[ck] === 'object') {
          opts[ck] = Object.assign({}, opts[ck], { __resolved: adapterProxy(ck) })
        }
      }

      var methods = Object.assign({}, makeCompat(), project.methods || {}, ov.methods || {})
      // lowest -> highest: the frame kit, the parent's call stubs, the
      // project's own functions, explicit overrides
      var functions = Object.assign(
        {},
        kitFunctions,
        stubsFor(Array.isArray(p.callNames) ? p.callNames : []),
        project.functions || {},
        ov.functions || {}
      )

      var router = opts.router
      if (router !== false) {
        router = Object.assign({}, router && typeof router === 'object' ? router : {}, {
          initialPath: p.path || '/'
        })
      }
      opts.router = router
      opts.methods = methods
      opts.functions = functions
      opts.context = Object.assign({}, project.context || {}, ov.context || {}, {
        methods: methods,
        functions: functions,
        window: win,
        document: doc
      })
      opts.themeRoot = doc.documentElement
      opts.document = doc
      opts.window = win
      opts.parent = doc.body

      var created
      try {
        created = S.create(shell, opts)
      } catch (err) {
        notify('boot-error', { message: String((err && err.message) || err) })
        return
      }
      Promise.resolve(created).then(
        function (result) {
          app = result
          try {
            var ds = result && result.context && result.context.designSystem
            if (ds) {
              // the app's own root element (create() returns it)
              ds.themeRoot = (result && result.node) || doc.documentElement
              themeCfg = ds
            }
          } catch (e) {}
          notify('ready', {})
        },
        function (err) {
          notify('boot-error', { message: String((err && err.message) || err) })
        }
      )
    }

    buildKit()

    port.onmessage = function (e) {
      var m = e.data
      if (!m || typeof m !== 'object') return
      if (m.id !== undefined && pending[m.id]) {
        var done = pending[m.id]
        delete pending[m.id]
        done(m)
        return
      }
      var S = win.Smbls
      if (m.type === 'mount') {
        mount(m.payload || {})
      } else if (m.type === 'theme') {
        if (S && typeof S.changeGlobalTheme === 'function' && typeof m.theme === 'string') {
          try {
            if (themeCfg) S.changeGlobalTheme(m.theme, themeCfg)
            else S.changeGlobalTheme(m.theme)
          } catch (err) {}
        }
      } else if (m.type === 'dir') {
        if (m.dir === 'rtl' || m.dir === 'ltr') doc.documentElement.setAttribute('dir', m.dir)
      } else if (m.type === 'navigate') {
        if (app && typeof app.navigate === 'function' && typeof m.path === 'string') {
          try {
            app.navigate(m.path)
          } catch (err) {}
        }
      } else if (m.type === 'tool' && typeof m.name === 'string' && typeof m.source === 'string') {
        stopTool(m.name)
        var toolName = m.name
        try {
          var toolFn = win.eval('(' + m.source + ')')
          var stop = toolFn({
            win: win,
            doc: doc,
            emit: function (data) {
              notify('tool-event', { name: toolName, data: data })
            }
          })
          tools[toolName] = typeof stop === 'function' ? stop : null
        } catch (err) {
          notify('tool-event', { name: toolName, data: { error: String((err && err.message) || err) } })
        }
      } else if (m.type === 'tool-stop' && typeof m.name === 'string') {
        stopTool(m.name)
      }
    }
  }

  function onMessage(e) {
    if (booted) return
    var d = e && e.data
    if (!d || d.type !== 'smbls:user-code-boot') return
    if (e.source !== win.parent) return
    var port = e.ports && e.ports[0]
    if (!port) return
    booted = true
    win.removeEventListener('message', onMessage)
    start(port)
    port.postMessage({ method: 'booted', args: {} })
  }

  shimStorage()
  try {
    var stubs = makeStubs()
    // frank-allow FA409 — frame boot: smbls runtime registry contract, runs inside the sandboxed srcdoc before any element exists
    var store = win.__SMBLS_PKGS__ || (win.__SMBLS_PKGS__ = {})
    for (var name in stubs) {
      if (!store[name]) store[name] = stubs[name]
    }
  } catch (e) {}
  win.addEventListener('message', onMessage)
}
